import type { PointerEvent, ReactNode } from 'react';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { useLatestRef } from '@gonfalon/react/useLatestRef';
import type { TooltipContextType } from '@visx/xychart';
import { EventEmitterContext } from '@visx/xychart';

import { TOOLTIP_DISMISS_DELAY_MS, TOOLTIP_MIN_CLICK_DISTANCE_PX } from '../../constants';
import { useTooltipContext } from '../components/Tooltip/TooltipContext';

interface UseTooltipFreezeParams {
  /** Whether click-to-freeze functionality is enabled */
  enabled: boolean;
  /** Current snapped X position (SVG coordinate) - used when pinning */
  snappedXPosition: number | null;
  /** Current active X value (data value) - used when pinning. Can be number or string for categories. */
  activeXValue: number | string | null;
  /** Current tooltip content - stored when pinning */
  tooltipContent: ReactNode | null;
  /** Visx tooltip context - used to capture tooltip position when pinning */
  visxTooltipContext: TooltipContextType<object> | null;
  /** Whether the pointer is directly over this chart - only this chart should pin on click when synced */
  isDirectlyHovered: boolean;
}

/** Event shape from visx EventEmitter */
interface VisxPointerEvent {
  svgPoint?: { x: number; y: number };
  event?: PointerEvent;
}

export interface UseTooltipFreezeResult {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

/**
 * Hook for tooltip freeze interaction: click-to-pin and hover-to-dismiss.
 *
 * - On click (chart): captures visx tooltip state and pins the tooltip.
 * - When pinned, clicking on chart does nothing; tooltip dismisses when the pointer
 *   leaves the tooltip or the chart area (with a short buffer for SVG-to-tooltip transition).
 *
 * When multiple charts share an event emitter (synced), only the chart that was directly
 * under the pointer at pointer down will pin or unpin; isDirectlyHovered is captured at
 * pointer down so pin behavior is robust to pointerleave/pointerup ordering.
 *
 * Uses a single EventEmitter subscription for pointerdown, pointerup, and pointerout.
 */
export function useTooltipFreeze({
  enabled,
  snappedXPosition,
  activeXValue,
  tooltipContent,
  visxTooltipContext,
  isDirectlyHovered,
}: UseTooltipFreezeParams): UseTooltipFreezeResult {
  const { isPinned, pinTooltip, unpinTooltip } = useTooltipContext();
  const eventEmitter = useContext(EventEmitterContext);

  const pointerDownPosition = useRef<{ x: number; y: number } | null>(null);
  // Capture whether pointer down occurred on this chart (robust to pointerleave/pointerup ordering)
  const pointerDownOnThisChartRef = useRef(false);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentStateRef = useLatestRef({
    snappedXPosition,
    activeXValue,
    tooltipContent,
    isPinned,
    isDirectlyHovered,
  });

  const visxTooltipContextRef = useLatestRef(visxTooltipContext);

  const clearDismissTimer = useCallback(() => {
    if (dismissTimerRef.current !== null) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, []);

  const onMouseEnter = useCallback(() => {
    clearDismissTimer();
  }, [clearDismissTimer]);

  const onMouseLeave = useCallback(() => {
    clearDismissTimer();
    unpinTooltip();
  }, [clearDismissTimer, unpinTooltip]);

  useEffect(() => {
    if (!enabled || !eventEmitter) {
      clearDismissTimer();
      return;
    }

    const handlePointerDown = (params?: VisxPointerEvent) => {
      pointerDownOnThisChartRef.current = currentStateRef.current.isDirectlyHovered;
      if (params?.svgPoint) {
        pointerDownPosition.current = {
          x: params.svgPoint.x,
          y: params.svgPoint.y,
        };
      }
    };

    const handlePointerUp = (params?: VisxPointerEvent) => {
      if (!params?.svgPoint || !pointerDownPosition.current) {
        pointerDownPosition.current = null;
        pointerDownOnThisChartRef.current = false;
        return;
      }

      const dx = params.svgPoint.x - pointerDownPosition.current.x;
      const dy = params.svgPoint.y - pointerDownPosition.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      pointerDownPosition.current = null;

      if (distance > TOOLTIP_MIN_CLICK_DISTANCE_PX) {
        pointerDownOnThisChartRef.current = false;
        return;
      }

      // Only the chart that had the pointer at down should pin/unpin (synced charts all receive pointerup)
      const didPointerDownOnThisChart = pointerDownOnThisChartRef.current;
      pointerDownOnThisChartRef.current = false;
      if (!didPointerDownOnThisChart) {
        return;
      }

      const {
        snappedXPosition: currentX,
        activeXValue: currentValue,
        tooltipContent: currentContent,
        isPinned: currentlyPinned,
      } = currentStateRef.current;

      if (currentlyPinned) {
        // Already pinned — unpin on click anywhere on chart (toggle behavior)
        unpinTooltip();
        return;
      }
      if (currentX !== null && currentValue !== null && currentContent !== null) {
        const ctx = visxTooltipContextRef.current;
        const tooltipLeft = ctx?.tooltipLeft ?? params.svgPoint.x;
        const tooltipTop = ctx?.tooltipTop ?? params.svgPoint.y;
        const shouldPin = ctx?.tooltipOpen || currentContent !== null;

        if (shouldPin) {
          pinTooltip({
            xPosition: currentX,
            xValue: currentValue,
            content: currentContent,
            visxData: {
              tooltipLeft,
              tooltipTop,
              tooltipData: ctx?.tooltipData ?? {},
            },
          });
        }
      }
    };

    const handlePointerOut = () => {
      clearDismissTimer();
      if (!currentStateRef.current.isPinned) {
        return;
      }
      dismissTimerRef.current = setTimeout(() => {
        dismissTimerRef.current = null;
        unpinTooltip();
      }, TOOLTIP_DISMISS_DELAY_MS);
    };

    const handlePointerMove = () => {
      clearDismissTimer();
    };

    eventEmitter.on('pointerdown', handlePointerDown);
    eventEmitter.on('pointerup', handlePointerUp);
    eventEmitter.on('pointerout', handlePointerOut);
    eventEmitter.on('pointermove', handlePointerMove);

    return () => {
      clearDismissTimer();
      eventEmitter.off('pointerdown', handlePointerDown);
      eventEmitter.off('pointerup', handlePointerUp);
      eventEmitter.off('pointerout', handlePointerOut);
      eventEmitter.off('pointermove', handlePointerMove);
    };
  }, [enabled, eventEmitter, pinTooltip, unpinTooltip, clearDismissTimer, currentStateRef, visxTooltipContextRef]);
  return { onMouseEnter, onMouseLeave };
}
